// settingsStore.js
import { Settings } from './settings';
import { onSnapshot, doc } from 'firebase/firestore'; // Import onSnapshot and doc for real-time updates
import { db } from '@/config/firebase'; // Assuming db is available

class SettingsStore {
  // Updated default values to include audio settings
  values = {
    sensitivity: 20.0,
    invertMouse: false,
    play_sound: true, // Master switch
    musicVolume: 0.5,
    sfxVolume: 0.5,
    musicEnabled: true,
    sfxEnabled: true,
    volume: 0.5 // Deprecated or Master Volume? Let's keep it as legacy Master or remove. User said "volume" 0-100. Let's assume Master Volume request.
    // Actually user said "volume should increase by factor of 10 (0-100)".
  };

  subscribers = new Set();
  settingsInstance = null;
  unsubscribeSnapshot = null; // Store unsubscribe function for real-time listener

  async load(userId) {
    if (!userId) {
      // console.warn('[SettingsStore] Cannot load settings without a userId.');
      // Set defaults and notify subscribers if no user
      this.values = { ...this.values }; // Ensure a fresh copy of defaults
      this.notifySubscribers();
      return;
    }

    this.settingsInstance = new Settings(userId);

    // Clean up previous listener if it exists
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
      this.unsubscribeSnapshot = null;
    }

    // Set up real-time listener for user settings
    const docRef = doc(db, 'userSettings', userId);
    this.unsubscribeSnapshot = onSnapshot(docRef, (snapshot) => {
      let loadedValues = {};
      if (snapshot.exists()) {
        loadedValues = snapshot.data();
        //  console.log('[SettingsStore] Real-time update:', loadedValues);
      } else {
        //  console.log('[SettingsStore] User settings document does not exist.');
      }

      // Use the Settings class's logic to merge with defaults and handle undefined values
      // Temporarily create a Settings instance to use its merging logic
      const tempSettings = new Settings(userId); // userId is needed for constructor but not used in this merge context
      tempSettings.values = { ...tempSettings.defaults, ...loadedValues }; // Merge defaults and loaded data

      // Update the store's values and notify subscribers
      this.values = tempSettings.values;
      this.notifySubscribers();

    }, (error) => {
      console.error('[SettingsStore] Error listening to settings:', error);
      // Optionally, revert to defaults or show an error message on listener error
      this.values = { ...new Settings(userId).defaults }; // Revert to defaults on error
      this.notifySubscribers();
    });

    // Also perform an initial load using the Settings class's load method
    // This ensures we have settings immediately if the snapshot listener
    // takes a moment to fire.
    try {
      const initialLoaded = await this.settingsInstance.load();
      // Merge initial load with current values (which might have been set by the first snapshot)
      this.values = { ...this.values, ...initialLoaded };
      this.notifySubscribers(); // Notify after initial load
    } catch (error) {
      console.error('[SettingsStore] Initial load failed:', error);
      // The snapshot listener error handler will also revert to defaults if needed.
    }


  }

  async save(newValues) {
    // Merge new values with current values to ensure all settings are saved
    const updatedValues = { ...this.values, ...newValues };
    this.values = updatedValues; // Update store's values immediately

    if (this.settingsInstance) {
      await this.settingsInstance.save(updatedValues); // Save to Firestore!
    } else {
      console.warn('[SettingsStore] Cannot save settings: settingsInstance not initialized.');
    }

    // Notify subscribers after saving and updating values
    this.notifySubscribers();
  }

  onChange(callback) {
    this.subscribers.add(callback);
    // Immediately push current state when subscribing
    callback(this.values);
    return () => this.subscribers.delete(callback);
  }

  notifySubscribers() {
    for (const cb of this.subscribers) {
      try {
        cb(this.values);
      } catch (e) {
        console.error('Error in settingsStore subscriber:', e);
      }
    }
  }

  // Add a dispose method to clean up the listener
  dispose() {
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
      this.unsubscribeSnapshot = null;
      console.log('[SettingsStore] Unsubscribed from settings listener.');
    }
    this.subscribers.clear();
    this.settingsInstance = null; // Clear settings instance
  }
}

export const settingsStore = new SettingsStore();
